/**
 * ShareForReview.jsx
 * Creating a link that sends posts to someone for approval.
 *
 * Two things this dialog has to be honest about, because both are easy to get
 * wrong and expensive to discover later:
 *
 *   The link is a credential. Anyone who has it sees the content, so the
 *   expiry is a required choice rather than a hidden default, and the link
 *   itself is shown once and never again.
 *
 *   The reviewer cannot publish anything. That is stated on this screen as
 *   well as on theirs, so nobody sends a link believing it hands over control.
 */

import { useState } from 'react';
import { api } from '../services/api.js';
import { Check, Warning, Users } from './Icons.jsx';

export default function ShareForReview({ brand, campaignId = null, postIds = [], onClose }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [days, setDays] = useState(14);
  const [canDecide, setCanDecide] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      setCreated(
        await api.reviews.create({
          brandId: brand.id,
          name: name.trim(),
          message: message.trim(),
          campaignId,
          postIds,
          days: Number(days),
          canDecide,
        }),
      );
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // ---------- after it exists ----------
  if (created) {
    return (
      <div className="modal-backdrop">
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h2>
            <Check size={19} /> Link ready
          </h2>

          <div className="notice notice--info">
            <Warning />
            <span>{created.warning}</span>
          </div>

          <div className="field">
            <label htmlFor="share-url">Send them this</label>
            <input
              id="share-url"
              readOnly
              value={created.url}
              onFocus={(e) => e.target.select()}
              style={{ fontFamily: 'var(--mono)', fontSize: 12 }}
            />
          </div>

          <div className="btn-row">
            <button
              className="btn btn--primary"
              onClick={() => {
                navigator.clipboard?.writeText(created.url);
                setCopied(true);
              }}
            >
              {copied ? 'Copied' : 'Copy the link'}
            </button>
            <button className="btn" onClick={onClose}>
              Done
            </button>
          </div>

          <p className="hint" style={{ marginTop: 12 }}>
            Anyone with this link can see these posts for the next {created.expiresInDays} days.
            They cannot publish or schedule anything — whatever they say comes back to you as
            feedback. Withdraw it at any time from the Reviews list.
          </p>
        </div>
      </div>
    );
  }

  // ---------- setting it up ----------
  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>
          <Users size={19} /> Send for approval
        </h2>

        <p className="hint">
          {campaignId
            ? 'Every post in this campaign.'
            : `${postIds.length} post${postIds.length === 1 ? '' : 's'}.`}{' '}
          They will not need a Kreeboom account.
        </p>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="share-name">What to call it</label>
          <input
            id="share-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="September, week one"
          />
          <p className="hint">Shown at the top of their page, and how you will recognise it later.</p>
        </div>

        <div className="field">
          <label htmlFor="share-message">A note for them (optional)</label>
          <textarea
            id="share-message"
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Anything you would change before these go out on Monday?"
          />
        </div>

        <div className="field">
          <label htmlFor="share-days">Stops working after</label>
          <select id="share-days" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={3}>3 days</option>
            <option value={7}>A week</option>
            <option value={14}>A fortnight</option>
            <option value={30}>A month</option>
            <option value={90}>Three months</option>
          </select>
          <p className="hint">
            The link is the only thing guarding this content, so it expires. Shorter is safer.
          </p>
        </div>

        <label className="checkline">
          <input type="checkbox" checked={canDecide} onChange={(e) => setCanDecide(e.target.checked)} />
          <span>
            <b>Let them approve and comment.</b>
            <span className="checkline__hint">
              {canDecide
                ? 'They can mark each post approved or needing changes, and leave a note. None of it publishes anything.'
                : 'Off — they can look, and nothing else.'}
            </span>
          </span>
        </label>

        <div className="btn-row" style={{ marginTop: 14 }}>
          <button className="btn btn--primary" onClick={create} disabled={busy}>
            {busy ? 'Creating…' : 'Create the link'}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
